import type { ImportSourcePreset } from './types'
import { ModuleTypeEnum } from '../../types/module-type'

/**
 * Time-tracking exports. All three land in TIME_ENTRY, which imports HOURS
 * ONLY: every rate, amount and invoice flag maps to `null` on purpose (see the
 * refusals on TimeEntryImportService). Source tasks are only linked where the
 * source's "task" is a real work item.
 */

/**
 * Harvest → Reports → Detailed time → Export → CSV/Excel.
 * Header row verified against a published sample export:
 * Date, Client, Project, Project Code, Task, Notes, Hours, Hours Rounded,
 * Billable?, Invoiced?, First Name, Last Name, Roles, Employee?, Billable Rate,
 * Billable Amount, Cost Rate, Cost Amount, Currency, External Reference URL.
 */
export const HARVEST_TIME_PRESET: ImportSourcePreset = {
  id: 'harvest-time',
  sourceSystem: 'HARVEST',
  sourceLabel: 'Harvest',
  exportLabel: 'time export',
  moduleType: ModuleTypeEnum.TIME_ENTRY,
  fingerprint: {
    requireAll: [['date'], ['hours'], ['firstname'], ['lastname'], ['project']],
    distinctive: [
      'hoursrounded',
      'invoiced',
      'projectcode',
      'employee',
      'externalreferenceurl',
    ],
  },
  columns: {
    date: 'entryDate',
    client: null,
    project: 'project',
    projectcode: null,
    // Harvest "tasks" are billing categories (Design, Development), not work
    // items. Linking them by name would attach hours to an unrelated task that
    // happens to share the name, so the column is left out.
    task: null,
    notes: 'description',
    hours: 'duration',
    hoursrounded: null,
    billable: 'billable',
    invoiced: null,
    approved: null,
    firstname: 'memberFirstName',
    lastname: 'memberLastName',
    roles: null,
    employee: null,
    billablerate: null,
    billableamount: null,
    costrate: null,
    costamount: null,
    currency: null,
    externalreferenceurl: null,
  },
  headersVerified: true,
  exportHint:
    'In Harvest, open Reports → Detailed time, pick the full date range, then Export → CSV.',
  notes: [
    'People are matched by first and last name, so each person must already be an active member with the same name.',
    'Hours import without rates or invoice status. Backdate rate cards to price them.',
    'Harvest task categories are not imported; entries link to their project.',
  ],
}

/**
 * Toggl Track → Reports → Detailed → Export → CSV.
 * Columns per Toggl's documentation: User, Email, Client, Project, Task,
 * Description, Billable, Start date, Start time, End date, End time, Duration,
 * Tags, Amount (). "Duration" is HH:MM:SS.
 */
export const TOGGL_TIME_PRESET: ImportSourcePreset = {
  id: 'toggl-time',
  sourceSystem: 'TOGGL',
  sourceLabel: 'Toggl',
  exportLabel: 'time export',
  moduleType: ModuleTypeEnum.TIME_ENTRY,
  fingerprint: {
    requireAll: [['user', 'email'], ['startdate'], ['duration']],
    distinctive: [
      'starttime',
      'endtime',
      'clientexternalreference',
      'projectexternalreference',
    ],
  },
  columns: {
    // The email resolves a person unambiguously; the display name is dropped
    // so it cannot shadow it.
    user: null,
    email: 'member',
    client: null,
    clientexternalreference: null,
    project: 'project',
    projectexternalreference: null,
    task: 'task',
    description: 'description',
    billable: 'billable',
    startdate: 'entryDate',
    starttime: null,
    enddate: null,
    endtime: null,
    duration: 'duration',
    tags: null,
    amount: null,
  },
  headersVerified: true,
  exportHint:
    'In Toggl Track, open Reports → Detailed, set the date range, then Export → Download CSV.',
  notes: [
    'Each entry is dated by its start day.',
    'Hours import without rates or amounts.',
  ],
}

/**
 * Clockify → Reports → Detailed → Export → Save as CSV.
 * Field list per Clockify's help center; the exact "Duration (h)" /
 * "Duration (decimal)" / "Billable Rate (USD)" spellings are from sample
 * exports and are NOT verified against current documentation.
 */
export const CLOCKIFY_TIME_PRESET: ImportSourcePreset = {
  id: 'clockify-time',
  sourceSystem: 'CLOCKIFY',
  sourceLabel: 'Clockify',
  exportLabel: 'time export',
  moduleType: ModuleTypeEnum.TIME_ENTRY,
  fingerprint: {
    requireAll: [
      ['user', 'email'],
      ['startdate'],
      ['durationdecimal', 'durationh'],
    ],
    distinctive: ['durationdecimal', 'durationh', 'billablerateusd'],
  },
  columns: {
    user: null,
    email: 'member',
    group: null,
    project: 'project',
    client: null,
    task: 'task',
    description: 'description',
    billable: 'billable',
    tags: null,
    startdate: 'entryDate',
    starttime: null,
    enddate: null,
    endtime: null,
    // Decimal wins; the clock-format twin is dropped so the two never feed the
    // same field.
    durationh: null,
    durationdecimal: 'duration',
    billablerateusd: null,
    billableamountusd: null,
    costrateusd: null,
    costamountusd: null,
  },
  headersVerified: false,
  exportHint:
    'In Clockify, open Reports → Detailed, set the date range, then Export → Save as CSV (a paid Clockify plan is needed for CSV).',
  notes: [
    'Each entry is dated by its start day.',
    'Hours import without rates or amounts.',
  ],
}

export const TIME_SOURCE_PRESETS: readonly ImportSourcePreset[] = [
  HARVEST_TIME_PRESET,
  TOGGL_TIME_PRESET,
  CLOCKIFY_TIME_PRESET,
]
